import type { StateCreator } from 'zustand';
import type { CartProductItem } from '@/features/cart/shared/types/cart.types';
import { calculateTotalPrice } from '@/features/cart/internal/helpers/calculate-total-price';
import { CartData } from '@/features/cart/shared/types/cart.types';
import { CartDataPrice, PromotionData } from '@/shared/types/prices';
import { trackAddToCart } from '@/features/gtm/shared/helper/track-add-to-cart';
import { PurchaseOption } from '@/features/games/shared/types/game-data';
import { cartItemToEcommerceParams } from '@/features/cart/shared/helpers/cart-item-to-ecommerce-params';
import { trackRemoveFromCart } from '@/features/gtm/shared/helper/track-remove-from-cart';
import { getLocaleFromPathname } from '@/shared/utils/i18n/locale-path';

// The cart handlers cannot read the site locale themselves (they live under /api); the store
// runs outside React, so the pathname is the source and the locale travels as a query param.
const cartApiUrl = (path: string) =>
  `${path}?locale=${getLocaleFromPathname(window.location.pathname)}`;

export interface CartSlice {
  cart: {
    cartId: string | null;
    totalPrice: {
      type: string;
      value: number;
      currencyCode: string;
    };
    items: CartProductItem[];
    showOverlay: boolean;
    checkout: {
      entityId: string | null;
      subtotal: CartDataPrice | null;
      grandTotal: CartDataPrice | null;
      taxTotal: CartDataPrice | null;
      coupons?: {
        code: string;
        discountedAmount: CartDataPrice;
      }[];
    };
  };
  cartLoading: boolean;
  cartInitialized: boolean;
  deleteCart: () => void;
  setCart: (cart: CartData) => void;
  setCheckout(checkout: CartSlice['cart']['checkout']): void;
  setCartLoading: (loading: boolean) => void;
  addItem: (
    purchaseOption: PurchaseOption,
    isWildMember?: boolean,
    promotion?: PromotionData,
  ) => Promise<void>;
  removeItem: (itemId: string, isWildMember?: boolean) => Promise<void>;
  setShowOverlay: (value: boolean) => void;
}

const withCartLoading =
  <T extends unknown[]>(fn: (...args: T) => Promise<void>) =>
  async (...args: T): Promise<void> => {
    const slice = getCartSliceInstance();
    slice.setCartLoading(true);
    try {
      await fn(...args);
    } finally {
      slice.setCartLoading(false);
    }
  };

// We’ll inject `get` dynamically later (this avoids circular references)
let getCartSliceInstance: () => CartSlice;

export const createCartSlice: StateCreator<CartSlice, [], [], CartSlice> = (set, get) => {
  // Assign the getter so our helper can access store methods
  getCartSliceInstance = get;

  return {
    cart: {
      cartId: null,
      items: [],
      showOverlay: false,
      totalPrice: {
        type: 'total',
        value: 0,
        currencyCode: 'USD',
      },
      checkout: {
        entityId: null,
        subtotal: null,
        grandTotal: null,
        taxTotal: null,
      },
    },

    cartLoading: false,
    cartInitialized: false,

    setCartLoading: (loading) => set({ cartLoading: loading }),

    setCheckout: (checkout: CartSlice['cart']['checkout']) => {
      set((state) => ({
        cart: {
          ...state.cart,
          checkout: {
            ...state.cart.checkout,
            entityId: checkout?.entityId || null,
            subtotal: checkout?.subtotal || null,
            grandTotal: checkout?.grandTotal || null,
            taxTotal: checkout?.taxTotal || null,
            coupons: Array.isArray(checkout?.coupons)
              ? checkout?.coupons
              : (state.cart.checkout?.coupons ?? undefined),
          },
        },
      }));
    },

    setCart: (cartData: CartData) => {
      set((state) => ({
        cart: {
          ...state.cart,
          cartId: cartData.cartId,
          items: cartData.items || [],
          totalPrice: cartData.totalPrice || state.cart.totalPrice,
          checkout: {
            ...state.cart.checkout,
            entityId: cartData.checkout?.entityId || null,
            subtotal: cartData.checkout?.subtotal || null,
            grandTotal: cartData.checkout?.grandTotal || null,
            taxTotal: cartData.checkout?.taxTotal || null,
            coupons: Array.isArray(cartData.checkout?.coupons)
              ? cartData.checkout?.coupons
              : (state.cart.checkout?.coupons ?? undefined),
          },
        },
        cartInitialized: true,
      }));
    },

    deleteCart: () => {
      const { cart } = get();
      if (!cart.cartId) return;

      fetch(cartApiUrl('/api/cart'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      })
        .then(() => {
          set((state) => ({
            cart: {
              ...state.cart,
              cartId: null,
              items: [],
              totalPrice: { type: 'total', value: 0, currencyCode: 'USD' },
            },
          }));
        })
        .catch((error) => {
          console.error('Failed to delete cart:', error);
        });
    },

    addItem: async (
      purchaseOption: PurchaseOption,
      isWildMember = false,
      promotion?: PromotionData,
    ) => {
      const { cart } = get();
      const cartRequestBody = {
        productEntityId: purchaseOption.productEntityId,
        optionEntityId: purchaseOption.optionEntityId,
        optionValueEntityId: purchaseOption.optionValueEntityId,
        currencyCode: purchaseOption.prices[0]?.currencyCode || 'USD',
      };

      if (!cart.cartId) {
        const res = await fetch(cartApiUrl('/api/cart'), {
          method: 'POST',
          body: JSON.stringify(cartRequestBody),
          headers: { 'Content-Type': 'application/json' },
        });

        const newCart = await res.json();

        set((state) => ({
          cart: {
            ...state.cart,
            cartId: newCart.cartId,
            items: newCart.items,
            totalPrice: calculateTotalPrice(newCart.items),
            showOverlay: true,
            checkout: {
              ...state.cart.checkout,
              entityId: newCart.checkout?.entityId || null,
              subtotal: newCart.checkout?.subtotal || null,
              grandTotal: newCart.checkout?.grandTotal || null,
              taxTotal: newCart.checkout?.taxTotal || null,
            },
          },
          cartInitialized: true,
        }));

        const newItem = newCart.items[0];
        const trackAddToCartParams = cartItemToEcommerceParams(newItem, isWildMember, promotion);
        trackAddToCart(trackAddToCartParams);
        return;
      }
      const res = await fetch(cartApiUrl(`/api/cart/${cart.cartId}/item`), {
        method: 'POST',
        body: JSON.stringify(cartRequestBody),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        set((state) => ({
          cart: { ...state.cart, showOverlay: false },
        }));
        console.error('Failed to add item to cart:', res.statusText);
        return;
      }

      const newItem = await res.json();

      const updatedItems = [...cart.items, newItem];

      set((state) => ({
        cart: {
          ...state.cart,
          items: updatedItems,
          totalPrice: calculateTotalPrice(updatedItems),
        },
        cartInitialized: true,
      }));

      const trackAddToCartParams = cartItemToEcommerceParams(newItem, isWildMember, promotion);
      trackAddToCart(trackAddToCartParams);
      return;
    },

    removeItem: withCartLoading(async (itemId: string, isWildMember: boolean = false) => {
      const { cart } = get();
      if (!cart.cartId) return;
      const cartId = cart.cartId;
      const toDelete = cart.items.find((i) => i.entityId === itemId);
      if (!toDelete) return;

      const res = await fetch(cartApiUrl(`/api/cart/${cartId}/item/${itemId}`), {
        method: 'DELETE',
        body: JSON.stringify({ itemId, cartId }),
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        console.error('Failed to remove item from cart:', res.statusText);
        return;
      }

      const status = await res.json();
      if (!status?.itemDeleted) {
        console.error('Failed to remove item from cart!');
        return;
      }

      const trackRemoveFromCartParams = cartItemToEcommerceParams(toDelete, isWildMember);
      trackRemoveFromCart(trackRemoveFromCartParams);

      const updatedItems = cart.items.filter((i) => i.entityId !== itemId);
      if (updatedItems.length === 0) {
        set((state) => ({
          cart: {
            ...state.cart,
            cartId: null,
            items: [],
            totalPrice: { type: 'total', value: 0, currencyCode: 'USD' },
            checkout: {
              ...state.cart.checkout,
              entityId: null,
              subtotal: null,
              grandTotal: null,
              taxTotal: null,
              coupons: undefined,
            },
          },
        }));
        return;
      }

      if (status.cart) {
        const updatedCart = status.cart;
        set((state) => ({
          cart: {
            ...state.cart,
            cartId: updatedCart.cartId,
            totalPrice: {
              ...state.cart.totalPrice,
              currencyCode: updatedCart.currencyCode || 'USD',
            },
            items: updatedCart.items || [],
            checkout: {
              ...state.cart.checkout,
              entityId: updatedCart.checkout?.entityId || null,
              subtotal: updatedCart.checkout?.subtotal || null,
              grandTotal: updatedCart.checkout?.grandTotal || null,
              taxTotal: updatedCart.checkout?.taxTotal || null,
              coupons: Array.isArray(updatedCart.checkout?.coupons)
                ? updatedCart.checkout?.coupons
                : (state.cart.checkout?.coupons ?? undefined),
            },
          },
        }));
      }
    }),

    setShowOverlay: (value: boolean) => {
      set((state) => ({
        cart: { ...state.cart, showOverlay: value },
      }));
    },
  };
};
